import React, { useEffect, useState } from 'react';
import {
  Badge, Button, DataTable, Input, KV, Modal, PageHead, SearchBox, StatCard,
  Tabs, Textarea, dateTimeStr, inr, num, titleCase,
} from '../components/ui';
import useList from '../hooks/useList';
import { paymentApi } from '../api';
import { useToast } from '../context/ToastContext';

const Payments = () => {
  const toast = useToast();
  const [tab, setTab] = useState('transactions');
  const [summary, setSummary] = useState(null);

  const txns = useList(paymentApi.list, { status: '', search: '', mode: '' });
  const dues = useList(paymentApi.outstanding, {}, { auto: false });

  const [detail, setDetail] = useState(null);
  const [refund, setRefund] = useState(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [busy, setBusy] = useState(false);

  const loadSummary = () => paymentApi.summary().then(res => setSummary(res.data)).catch(() => {});
  useEffect(() => { loadSummary(); }, []);

  useEffect(() => {
    if (tab === 'outstanding') dues.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const doRefund = async () => {
    setBusy(true);
    try {
      await paymentApi.refund(refund._id, { amount: Number(refundAmount) || undefined, reason: refundReason });
      toast.success('Refund processed');
      setRefund(null);
      setDetail(null);
      txns.reload();
      loadSummary();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const releaseEscrow = async payment => {
    try {
      await paymentApi.releaseEscrow(payment._id);
      toast.success('Escrow released to the seller');
      txns.reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const txnColumns = [
    {
      key: 'paymentNumber',
      label: 'Payment',
      render: row => (
        <>
          <div className="cell-strong mono">{row.paymentNumber}</div>
          <div className="cell-muted">{dateTimeStr(row.createdAt)}</div>
        </>
      ),
    },
    { key: 'order', label: 'Order', render: row => <span className="mono text-sm">{row.order?.orderNumber || '—'}</span> },
    {
      key: 'parties',
      label: 'Buyer → Seller',
      render: row => (
        <>
          <div>{row.buyer?.name || '—'}</div>
          <div className="cell-muted">→ {row.seller?.name || '—'}</div>
        </>
      ),
    },
    { key: 'amount', label: 'Amount', align: 'right', render: row => <strong>{inr(row.amount)}</strong> },
    {
      key: 'mode',
      label: 'Mode',
      render: row => (
        <>
          <Badge status={row.mode} tone="brand" dot={false}>{titleCase(row.mode)}</Badge>
          {row.gateway?.method && <div className="cell-muted mt-1">{row.gateway.method}</div>}
        </>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: row => (
        <div className="row" style={{ gap: 5 }}>
          <Badge status={row.status} />
          {row.isEscrow && !row.escrowReleasedAt && <Badge status="pending" dot={false}>Escrow</Badge>}
        </div>
      ),
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: row => (
        <div className="row-actions">
          {row.isEscrow && !row.escrowReleasedAt && row.status === 'paid' && (
            <Button size="sm" variant="soft" onClick={e => { e.stopPropagation(); releaseEscrow(row); }}>Release</Button>
          )}
          {row.status === 'paid' && (
            <Button
              size="sm" variant="ghost"
              onClick={e => { e.stopPropagation(); setRefund(row); setRefundAmount(String(row.amount)); setRefundReason(''); }}
            >
              Refund
            </Button>
          )}
        </div>
      ),
    },
  ];

  const dueColumns = [
    { key: 'orderNumber', label: 'Order', render: row => <span className="mono cell-strong">{row.orderNumber}</span> },
    { key: 'buyer', label: 'Buyer', render: row => row.buyer?.name || '—' },
    { key: 'seller', label: 'Seller', render: row => row.seller?.name || '—' },
    { key: 'grandTotal', label: 'Order value', align: 'right', render: row => inr(row.grandTotal) },
    { key: 'paid', label: 'Paid', align: 'right', render: row => inr(row.payment?.paidAmount) },
    { key: 'due', label: 'Outstanding', align: 'right', render: row => <strong style={{ color: 'var(--danger)' }}>{inr(row.payment?.dueAmount)}</strong> },
    { key: 'status', label: 'Order', render: row => <Badge status={row.status} /> },
  ];

  return (
    <>
      <PageHead title="Payments" subtitle="Collections, escrow and receivables across the platform." />

      {summary && (
        <div className="stat-grid stagger mb-2">
          <StatCard accent label="Total collected" value={inr(summary.totalCollected, true)} icon="₹" foot={`${num(summary.byStatus.paid.count)} successful payments`} />
          <StatCard label="This month" value={inr(summary.thisMonth, true)} icon="📈" />
          <StatCard label="Outstanding" value={inr(summary.outstanding, true)} icon="⏳" foot={`${num(summary.outstandingOrders)} orders unpaid`} onClick={() => setTab('outstanding')} />
          <StatCard label="Failed" value={num(summary.byStatus.failed.count)} icon="✕" />
          <StatCard label="Refunded" value={inr(summary.byStatus.refunded.amount, true)} icon="↩" />
        </div>
      )}

      <Tabs
        tabs={[{ value: 'transactions', label: 'Transactions' }, { value: 'outstanding', label: 'Receivables' }]}
        value={tab}
        onChange={setTab}
      />

      {tab === 'transactions' ? (
        <>
          <div className="filters">
            <SearchBox value={txns.filters.search} onChange={v => txns.setFilter('search', v)} placeholder="Search payment number…" />
            <select className="select" style={{ width: 170 }} value={txns.filters.status} onChange={e => txns.setFilter('status', e.target.value)}>
              <option value="">Any status</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>
            <select className="select" style={{ width: 180 }} value={txns.filters.mode} onChange={e => txns.setFilter('mode', e.target.value)}>
              <option value="">Any mode</option>
              <option value="online">Online (Razorpay)</option>
              <option value="bank_transfer">Bank transfer</option>
              <option value="credit">Credit</option>
              <option value="escrow">Escrow</option>
              <option value="cod">Cash on delivery</option>
            </select>
          </div>
          <DataTable columns={txnColumns} rows={txns.rows} loading={txns.loading} meta={txns.meta} onPage={txns.setPage} onRowClick={setDetail} />
        </>
      ) : (
        <>
          <div className="filters">
            <span className="text-muted text-sm">{dues.meta ? `${num(dues.meta.total)} orders with money outstanding` : ''}</span>
          </div>
          <DataTable columns={dueColumns} rows={dues.rows} loading={dues.loading} meta={dues.meta} onPage={dues.setPage} />
        </>
      )}

      <Modal open={!!detail} title={detail?.paymentNumber} subtitle="Payment details" onClose={() => setDetail(null)}>
        {detail && (
          <>
            <div className="row mb-2" style={{ gap: 8 }}>
              <Badge status={detail.status} />
              <Badge status={detail.mode} tone="brand" dot={false}>{titleCase(detail.mode)}</Badge>
            </div>
            <KV
              items={[
                { label: 'Amount', value: <strong>{inr(detail.amount)}</strong> },
                { label: 'Order', value: detail.order?.orderNumber || '—' },
                { label: 'Buyer', value: detail.buyer?.name },
                { label: 'Seller', value: detail.seller?.name },
                { label: 'Paid at', value: dateTimeStr(detail.paidAt) },
                detail.gateway?.orderId && { label: 'Gateway order', value: <span className="mono text-sm">{detail.gateway.orderId}</span> },
                detail.gateway?.paymentId && { label: 'Gateway payment', value: <span className="mono text-sm">{detail.gateway.paymentId}</span> },
                detail.gateway?.method && { label: 'Method', value: `${detail.gateway.method} ${detail.gateway.bank || detail.gateway.vpa || ''}` },
                detail.referenceNumber && { label: 'Reference', value: detail.referenceNumber },
                detail.remarks && { label: 'Remarks', value: detail.remarks },
                detail.failureReason && { label: 'Failure', value: detail.failureReason },
                detail.refund?.amount > 0 && { label: 'Refunded', value: `${inr(detail.refund.amount)} — ${detail.refund.reason}` },
              ]}
            />
          </>
        )}
      </Modal>

      <Modal
        open={!!refund}
        title="Process a refund"
        subtitle={refund?.paymentNumber}
        onClose={() => setRefund(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setRefund(null)}>Cancel</Button>
            <Button variant="danger" loading={busy} onClick={doRefund}>Refund {inr(Number(refundAmount) || 0)}</Button>
          </>
        }
      >
        <p className="text-muted mb-2">
          Online payments are refunded through Razorpay. Offline payments are recorded as refunded for reconciliation.
        </p>
        <Input label="Refund amount (₹)" type="number" max={refund?.amount} value={refundAmount} onChange={e => setRefundAmount(e.target.value)} hint={`Maximum ${inr(refund?.amount)}`} />
        <Textarea label="Reason" value={refundReason} onChange={e => setRefundReason(e.target.value)} placeholder="Order cancelled after payment" />
      </Modal>
    </>
  );
};

export default Payments;
